import { ApiError, ehRedeBloqueada } from '@/lib/api/errors'
import { logout, renovarAcesso, type RespostaDeAcesso } from '@/services/auth'
import { definirAcesso, esquemaDe, lerAcesso } from './acesso'
import { ouvirCanal, publicarNoCanal } from './canalEntreAbas'
import { bloquear, liberar } from './redeBloqueada'
import { esperarFimDaOperacao, estaAbaEstaSaindo, haSaidaPendente, limparSaidaPendente } from './sessaoEntreAbas'
import { segurarTrinco } from './trinco'

/**
 * A renovação única entre abas (faixa 25 §2–§4). Ver `renovacao.md`.
 *
 * - `renovada`: há access novo em memória (desta chamada ou de quem chegou antes);
 * - `sessao-morta`: o cookie não renova (401 `REFRESH_INVALID`, 403 `ORIGIN_FORBIDDEN`), ou esta aba já sai;
 * - `saida-pendente`: havia "Sair" não confirmado e o back continua sem responder — não entre;
 * - `indisponivel`: 503, rede, teto, resposta fora do contrato. NUNCA desloga (I5).
 * - `rede-bloqueada`: 403 `NETWORK_FORBIDDEN` — o IP está fora da rede permitida; o cookie FICA. Acende o bloqueio (`redeBloqueada.ts`). NUNCA desloga nem re-agenda.
 * Quem reage é o chamador: interceptor, agendador, socket, portão e login reagem diferente.
 */
export type ResultadoDaRenovacao =
  | { tipo: 'renovada' }
  | { tipo: 'sessao-morta' }
  | { tipo: 'saida-pendente' }
  | { tipo: 'indisponivel' }
  | { tipo: 'rede-bloqueada' }

/** Renovação "crua", de dentro de uma operação que já segura o trinco. Não aplica nada. */
export type RespostaDaRenovacao =
  | { tipo: 'ok'; acesso: RespostaDeAcesso }
  | { tipo: 'sessao-morta' }
  | { tipo: 'indisponivel' }
  | { tipo: 'rede-bloqueada' }

export interface OpcoesDaRenovacao {
  /** Access com que o pedido saiu (ou o atual, default). Se a memória já tem OUTRO, não chama a API. */
  acessoDoPedido?: string | null
}

/** Single-flight DENTRO da aba: N 401 juntos esperam a mesma renovação (I4). */
let emVoo: Promise<ResultadoDaRenovacao> | null = null

export function renovar({ acessoDoPedido = lerAcesso() }: OpcoesDaRenovacao = {}): Promise<ResultadoDaRenovacao> {
  emVoo ??= renovarNoTrinco(acessoDoPedido).finally(() => {
    emVoo = null
  })
  return emVoo
}

async function renovarNoTrinco(acessoDoPedido: string | null): Promise<ResultadoDaRenovacao> {
  // §2: toda renovação primeiro espera a operação anunciada (D9 da faixa 22). Durante a troca de senha
  // o back derruba a sessão velha antes de abrir a nova; um refresh com o cookie velho nessa janela leva
  // 401 e o 401 APAGA o cookie novo (mesmo nome e caminho). O trinco abaixo fecha o resto da janela (I13).
  await esperarFimDaOperacao()
  if (estaAbaEstaSaindo()) return { tipo: 'sessao-morta' }
  return segurarTrinco(async (): Promise<ResultadoDaRenovacao> => {
    if (estaAbaEstaSaindo()) return { tipo: 'sessao-morta' }
    // A marca é conferida DEPOIS de pegar o trinco e concluída com ele (sem pedir de novo).
    if (haSaidaPendente()) return concluirSaidaPendente()
    // Outra aba (ou esta, por outro caminho) já renovou enquanto o pedido esperava: usa esse.
    const agora = lerAcesso()
    if (agora !== null && agora !== acessoDoPedido) return { tipo: 'renovada' }
    const resposta = await renovarDentroDaOperacao()
    if (resposta.tipo !== 'ok') return resposta
    aplicarAcesso(resposta.acesso, 'renovacao')
    // Renovou = o IP voltou a passar na política: apaga o bloqueio (no-op se não havia). DEPOIS de aplicar — o socket
    // religa no `liberar()` e o handshake já lê o access novo da memória.
    liberar()
    return { tipo: 'renovada' }
  })
}

/**
 * `POST /auth/refresh` DIRETO: sem trinco, sem esperar anúncio, sem conferir a marca. Só pra quem já
 * segura o trinco (logout, logout-all, troca de senha, conclusão da saída pendente) — pedir o trinco de
 * novo enfileiraria atrás de si mesmo (§2 "sem reentrada"). Não aplica nada: quem chama decide.
 */
export async function renovarDentroDaOperacao(timeoutMs?: number): Promise<RespostaDaRenovacao> {
  try {
    return { tipo: 'ok', acesso: await renovarAcesso(timeoutMs) }
  } catch (erro) {
    if (ehSessaoMorta(erro)) return { tipo: 'sessao-morta' }
    if (ehRedeBloqueada(erro)) {
      // Faixa 28 §4: o cookie vale, a rede não. Nem sessão morta nem indisponível — e não é falha: sem log.
      bloquear()
      return { tipo: 'rede-bloqueada' }
    }
    console.warn('Renovação do acesso indisponível; a sessão continua.', descrever(erro))
    return { tipo: 'indisponivel' }
  }
}

/**
 * Só estes afirmam "o cookie não renova" (decisão 5). 400 `invalid_dpop_proof` (faixa 33 §5): a sessão é presa a uma
 * chave que este navegador não tem mais (IndexedDB apagado, outra chave) — repetir seria laço (B6); o back não apaga o
 * cookie e o login novo o sobrescreve. 403 `NETWORK_FORBIDDEN` é `rede-bloqueada`; o resto (inclusive a 2ª
 * `use_dpop_nonce` e a chave local que falhou, que chega como rede), `indisponivel`.
 */
function ehSessaoMorta(erro: unknown): boolean {
  if (!(erro instanceof ApiError)) return false
  return (
    (erro.status === 401 && erro.code === 'REFRESH_INVALID') ||
    (erro.status === 403 && erro.code === 'ORIGIN_FORBIDDEN') ||
    (erro.status === 400 && erro.code === 'invalid_dpop_proof')
  )
}

/** Pro log: nunca o corpo nem o token (I1). */
function descrever(erro: unknown): string {
  if (erro instanceof ApiError) return `HTTP ${erro.status} ${erro.code}`
  return erro instanceof Error ? erro.name : 'erro desconhecido'
}

/**
 * Access novo desta aba: memória (avisa agendador e socket) + entrega às outras abas. O esquema sai do `token_type`
 * da emissão (faixa 33 §5); a troca de senha de back antigo não manda → `Bearer`.
 */
export function aplicarAcesso(
  resposta: Pick<RespostaDeAcesso, 'access_token' | 'expires_in'> & { token_type?: string },
  origem: 'renovacao' | 'troca-senha',
): void {
  const esquema = esquemaDe(resposta.token_type)
  definirAcesso({ token: resposta.access_token, expiresIn: resposta.expires_in, origem, deOutraAba: false, esquema })
  publicarNoCanal({
    tipo: 'acesso',
    access_token: resposta.access_token,
    expires_in: resposta.expires_in,
    origem,
    token_type: esquema,
  })
}

/** Ponte: access que outra aba renovou entra na memória desta. Ligada pelo portão privado e pela tela de login. */
export function ligarAcessoDasOutrasAbas(): () => void {
  return ouvirCanal((mensagem) => {
    if (mensagem.tipo !== 'acesso' || estaAbaEstaSaindo()) return
    definirAcesso({
      token: mensagem.access_token,
      expiresIn: mensagem.expires_in,
      origem: mensagem.origem,
      deOutraAba: true,
      // Aba de build anterior não manda `token_type` → `Bearer`: se a sessão é presa, o 401 renova (com prova).
      esquema: esquemaDe(mensagem.token_type),
    })
    // Outra aba renovou = o navegador voltou a passar na rede (mesma máquina, mesmo IP): apaga o bloqueio desta.
    liberar()
  })
}

/**
 * Saída pendente (§6): renova e faz logout com o access recém-obtido pra matar o cookie. 204 ou 401 (na
 * renovação ou no logout) confirmam e apagam a marca → `sessao-morta`; 503/rede e 403 de rede deixam a marca → `saida-pendente`.
 * O access obtido aqui NÃO vai pra memória nem pro canal (decisão 4).
 */
async function concluirSaidaPendente(): Promise<ResultadoDaRenovacao> {
  const renovada = await renovarDentroDaOperacao()
  // 503/rede ou fora da rede permitida (cookie vivo): a saída não confirmou — a marca FICA.
  if (renovada.tipo === 'indisponivel' || renovada.tipo === 'rede-bloqueada') return { tipo: 'saida-pendente' }
  if (renovada.tipo === 'ok') {
    try {
      await logout({ acesso: { token: renovada.acesso.access_token, esquema: esquemaDe(renovada.acesso.token_type) } })
    } catch (erro) {
      if (!(erro instanceof ApiError && erro.status === 401)) {
        console.warn('Saída pendente ainda não confirmada pelo servidor.', descrever(erro))
        return { tipo: 'saida-pendente' }
      }
    }
  }
  limparSaidaPendente()
  return { tipo: 'sessao-morta' }
}
